import { DEFAULT_CLIENT_RETRY, DEFAULT_CLIENT_TIMEOUT } from '../constants'

import { KyTransportAdapter } from './transport'

import type {
  CreateClientOptions,
  HttpClientConfig,
  HttpResponse,
  HttpRequest,
  RequestOptions,
  HttpMethod,
} from '../types'

export class HttpClient {
  private readonly config: HttpClientConfig
  private readonly transport: KyTransportAdapter

  constructor(config: HttpClientConfig) {
    this.config = {
      ...config,
      timeout: config.timeout ?? DEFAULT_CLIENT_TIMEOUT,
      retry: config.retry ?? false,
    }

    this.transport = new KyTransportAdapter({
      baseURL: this.config.baseURL,
      timeout: this.config.timeout,
      headers: this.config.headers,
      retry: this.config.retry,
    })
  }

  get<T>(path: string, opts?: RequestOptions): Promise<HttpResponse<T>> {
    return this.request<T>('GET', path, undefined, opts)
  }

  post<T>(path: string, body?: unknown, opts?: RequestOptions): Promise<HttpResponse<T>> {
    return this.request<T>('POST', path, body, opts)
  }

  put<T>(path: string, body?: unknown, opts?: RequestOptions): Promise<HttpResponse<T>> {
    return this.request<T>('PUT', path, body, opts)
  }

  patch<T>(path: string, body?: unknown, opts?: RequestOptions): Promise<HttpResponse<T>> {
    return this.request<T>('PATCH', path, body, opts)
  }

  delete<T>(path: string, opts?: RequestOptions): Promise<HttpResponse<T>> {
    return this.request<T>('DELETE', path, undefined, opts)
  }

  private async request<T>(
    method: HttpMethod,
    path: string,
    body?: unknown,
    opts?: RequestOptions,
  ): Promise<HttpResponse<T>> {
    const mergedRetry =
      opts?.retry !== undefined
        ? opts.retry === false
          ? false
          : { ...DEFAULT_CLIENT_RETRY, ...opts.retry }
        : this.config.retry

    const httpRequest: HttpRequest = {
      url: path,
      method,
      headers: { ...this.config.headers, ...opts?.headers },
      body,
      signal: opts?.signal,
      timeout: opts?.timeout ?? this.config.timeout,
      searchParams: opts?.searchParams,
      retry: mergedRetry,
    }

    return this.transport.execute<T>(httpRequest)
  }
}

export function createClient(baseURL: string, options?: CreateClientOptions): HttpClient {
  const config: HttpClientConfig = { baseURL, ...options }
  return new HttpClient(config)
}
